import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowUpCircle, Loader2, Clock3, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const WaitlistPage = () => {
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promotingId, setPromotingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchWaitlist = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/waitlist`, { headers: getAuthHeaders() });
      setWaitlist(res.data);
    } catch {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWaitlist(); }, [fetchWaitlist]);

  const promote = async (attendee) => {
    if (!window.confirm(`Promote ${attendee.first_name} ${attendee.last_name} off the waitlist? They'll be moved to Pending and get a portal login.`)) return;
    setPromotingId(attendee.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/waitlist/${attendee.id}/promote`, {}, { headers: getAuthHeaders() });
      toast.success(`${attendee.first_name} promoted${res.data.login_created ? ' — portal login created' : ''}`);
      fetchWaitlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to promote');
    } finally {
      setPromotingId(null);
    }
  };

  const importCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/waitlist/import`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      toast.success(`Waitlist import: ${d.created} added · ${d.updated} already known · ${d.skipped} skipped`, { duration: 6000 });
      if (d.errors?.length) {
        toast.error(`${d.errors.length} row(s) had errors — see console`);
        console.warn('[Waitlist import errors]', d.errors);
      }
      fetchWaitlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center h-[50vh]"><Loader2 className="w-6 h-6 text-gray-300 animate-spin" /></div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="waitlist-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Waitlist Queue</h1>
          <p className="text-gray-600 mt-1">Ordered by signup time — first in line at the top. Promote someone when a spot opens.</p>
        </div>
        <div>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="waitlist-csv-input" />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-waitlist-csv-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            Import Waitlist CSV
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          {waitlist.length === 0 ? (
            <div className="py-12 text-center text-gray-400" data-testid="waitlist-empty">
              <Clock3 className="w-8 h-8 mx-auto mb-3 text-gray-200" />
              The waitlist is empty
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="waitlist-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-4">#</th>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Phone</th>
                    <th className="py-2 pr-4">Signed Up</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((a, i) => (
                    <tr key={a.id} className="border-b border-gray-50">
                      <td className="py-3 pr-4">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${i === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {i + 1}
                        </span>
                      </td>
                      <td className="py-3 pr-4 font-medium text-gray-900">{a.first_name} {a.last_name}</td>
                      <td className="py-3 pr-4 text-gray-500">{a.email}</td>
                      <td className="py-3 pr-4 text-gray-500">{a.phone || '—'}</td>
                      <td className="py-3 pr-4 text-gray-500 text-xs">{a.registration_date || (a.created_at ? new Date(a.created_at).toLocaleDateString() : '—')}</td>
                      <td className="py-3 text-right">
                        <Button
                          size="sm"
                          onClick={() => promote(a)}
                          disabled={promotingId === a.id}
                          className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
                          data-testid={`promote-btn-${a.id}`}
                        >
                          {promotingId === a.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5 mr-1.5" />}
                          Promote
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WaitlistPage;
